import { useState, useCallback } from 'react';
import type { ArrowType } from '../components/ArrowSection';
import type { TemplateMode } from '../components/BatchUpload';
import { ARROW_TYPES } from '../constants/reactionScheme';
import type { OverlayTextHorizontalAlign } from '../components/OverlayTextSvg';

export function useReactionSchemeState() {
  // Core state
  const [templateMode, setTemplateMode] = useState<TemplateMode>('reaction');
  const [reactantConfig, setReactantConfig] = useState('');
  const [productConfig, setProductConfig] = useState('');
  const [topText, setTopText] = useState('');
  const [bottomText, setBottomText] = useState('');
  const [arrowType, setArrowType] = useState<ArrowType>('single');
  const [reactantViewBox, setReactantViewBox] = useState<string | null>(null);
  const [productViewBox, setProductViewBox] = useState<string | null>(null);
  const [altText, setAltText] = useState('');
  // Dev-mode override; empty by default so the server-resolved reference wins.
  const [systemPrompt, setSystemPrompt] = useState('');
  const [contextPrompt, setContextPrompt] = useState('');
  const [selectedContextOption, setSelectedContextOption] = useState('none');
  const [jsonInput, setJsonInput] = useState('');
  const [loadError, setLoadError] = useState('');

  // Text overlay state
  const [reactantOverlayText, setReactantOverlayText] = useState('');
  const [productOverlayText, setProductOverlayText] = useState('');
  /** Pixel center/anchor in slot SVG space; null = default placement */
  const [reactantOverlayPosition, setReactantOverlayPosition] = useState<{ x: number; y: number } | null>(null);
  const [productOverlayPosition, setProductOverlayPosition] = useState<{ x: number; y: number } | null>(null);
  const [reactantOverlayTextAlign, setReactantOverlayTextAlign] = useState<OverlayTextHorizontalAlign | null>(null);
  const [productOverlayTextAlign, setProductOverlayTextAlign] = useState<OverlayTextHorizontalAlign | null>(null);

  /** Clears reactant, product, arrow text/type, overlays, and molecule viewBoxes */
  const resetMoleculeArrowProduct = useCallback(() => {
    setReactantConfig('');
    setProductConfig('');
    setTopText('');
    setBottomText('');
    setArrowType('single');
    setReactantViewBox(null);
    setProductViewBox(null);
    setReactantOverlayText('');
    setProductOverlayText('');
    setReactantOverlayPosition(null);
    setProductOverlayPosition(null);
    setReactantOverlayTextAlign(null);
    setProductOverlayTextAlign(null);
  }, []);

  // Clear all state
  const clearState = useCallback(() => {
    resetMoleculeArrowProduct();
    setAltText('');
    setJsonInput('');
    setLoadError('');
    setSystemPrompt('');
    setContextPrompt('');
    setSelectedContextOption('none');
  }, [resetMoleculeArrowProduct]);

  // Cycle through arrow types
  const cycleArrowType = useCallback(() => {
    setArrowType((current) => {
      const currentIndex = ARROW_TYPES.indexOf(current);
      const nextIndex = (currentIndex + 1) % ARROW_TYPES.length;
      return ARROW_TYPES[nextIndex];
    });
  }, []);

  return {
    // State values
    templateMode,
    reactantConfig,
    productConfig,
    topText,
    bottomText,
    arrowType,
    reactantViewBox,
    productViewBox,
    altText,
    systemPrompt,
    contextPrompt,
    selectedContextOption,
    jsonInput,
    loadError,
    reactantOverlayText,
    productOverlayText,
    reactantOverlayPosition,
    productOverlayPosition,
    reactantOverlayTextAlign,
    productOverlayTextAlign,

    // State setters
    setTemplateMode,
    setReactantConfig,
    setProductConfig,
    setTopText,
    setBottomText,
    setArrowType,
    setReactantViewBox,
    setProductViewBox,
    setAltText,
    setSystemPrompt,
    setContextPrompt,
    setSelectedContextOption,
    setJsonInput,
    setLoadError,
    setReactantOverlayText,
    setProductOverlayText,
    setReactantOverlayPosition,
    setProductOverlayPosition,
    setReactantOverlayTextAlign,
    setProductOverlayTextAlign,

    // Functions
    clearState,
    resetMoleculeArrowProduct,
    cycleArrowType,
  };
}